import React from 'react';
import { UserEntity } from '@backstage/catalog-model';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import { TeamsIcon } from '../icons/TeamsIcon';
import { TeamsChatOptions, teamsChatUrl, teamsChatUserOf } from './teamsChat';

/**
 * Props for {@link TeamsChatLink}.
 *
 * @public
 */
export type TeamsChatLinkProps = TeamsChatOptions & {
  /**
   * Shown next to the icon. Left out, the link is the icon alone, small
   * enough to sit after a name or an address.
   */
  label?: string;
};

/**
 * Opens a chat in Microsoft Teams, with the Teams logo so that it reads as
 * that and not as one more link into the catalog. Nothing is shown for no one
 * to chat with.
 *
 * @public
 */
export const TeamsChatLink = (props: Readonly<TeamsChatLinkProps>) => {
  const { label, ...options } = props;
  if (options.users.length === 0) {
    return null;
  }
  const href = teamsChatUrl(options);
  const tooltip =
    options.users.length > 1
      ? `Group chat in Teams with ${options.users.length} people`
      : 'Chat in Teams';

  if (label) {
    return (
      <Tooltip title={tooltip}>
        <Button
          size="small"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<TeamsIcon fontSize="small" />}
        >
          {label}
        </Button>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={tooltip}>
      <IconButton
        size="small"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={tooltip}
      >
        <TeamsIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

/**
 * How many people Teams lets into a group chat. A list longer than that gets
 * no group chat rather than one Teams refuses to open.
 */
const MAX_GROUP_CHAT_SIZE = 250;

/**
 * Props for {@link TeamsGroupChatLink}.
 *
 * @public
 */
export type TeamsGroupChatLinkProps = {
  members: UserEntity[];
  /** The name of the chat, see {@link TeamsChatOptions}. */
  topicName?: string;
};

/**
 * One chat in Teams with everyone of a member list. Shown for two people or
 * more - with one it would be the chat their own card offers - and up to as
 * many as a group chat holds.
 *
 * @public
 */
export const TeamsGroupChatLink = (
  props: Readonly<TeamsGroupChatLinkProps>,
) => {
  const users = [
    ...new Set(
      props.members.flatMap(member => {
        const user = teamsChatUserOf(member);
        return user ? [user] : [];
      }),
    ),
  ];
  if (users.length < 2 || users.length > MAX_GROUP_CHAT_SIZE) {
    return null;
  }
  return (
    <TeamsChatLink
      users={users}
      topicName={props.topicName}
      label="Group chat"
    />
  );
};
